export type TrainingDomain = 'technical' | 'tactical' | 'physical' | 'mental' | 'set_piece';

export type TrainingResource = {
  label: string;
  organisation: string;
  coachingPoints: string[];
  progression: string;
  individualUse: string;
  teamUse: string;
  workRest: string;
  sourceLabel: string;
  sourceUrl: string;
  sourceType: 'FIFA' | 'Real Madrid' | 'Evidence';
};

export type TeamTrainingTopic = {
  id: string;
  label: string;
  domain: TrainingDomain;
  objective: string;
  sessionStructure: string[];
  coachingPoints: string[];
  sourceLabel: string;
  sourceUrl: string;
};

export const PLAYER_TRAINING: Record<string, TrainingResource> = {
  first_touch: {
    label: 'First touch',
    organisation: 'Receive from multiple angles into a defined next-action zone. Alternate open body, pressure side and supporting foot.',
    coachingPoints: ['Scan before the ball arrives', 'Receive away from pressure', 'First touch must prepare the next action', 'Use both feet'],
    progression: 'Unopposed repetition → passive defender → directional 1v1/2v2 → small-sided game with touch condition.',
    individualUse: 'Wall/coach service, 4 gates, 2–3 sets of 6–10 quality receptions per angle.',
    teamUse: 'Directional possession game where the first touch must break a line or protect the ball.',
    workRest: 'Quality-led: 45–75 s blocks, generous recovery, stop before technique deteriorates.',
    sourceLabel: 'FIFA Training Centre – Technical Activation',
    sourceUrl: 'https://www.fifatrainingcentre.com/en/practice/technical-activation/technical-activation-part-1--introduction.php',
    sourceType: 'FIFA',
  },
  receiving_orientation: {
    label: 'Receiving orientation',
    organisation: 'Check shoulder before receiving, open hips, play through a different gate than the service arrived from.',
    coachingPoints: ['Scan early', 'Arrive side-on', 'Manipulate defender with body shape', 'Accelerate after first contact'],
    progression: 'Technical activation → 3v1/4v2 rondo → positional possession → opposed phase play.',
    individualUse: 'Coach/server provides colour/number cue just before the pass; player must scan and exit the called gate.',
    teamUse: 'Rondo/possession with bonus for receiving on the far foot and playing forward.',
    workRest: '4–6 x 60–90 s technical blocks; 45–60 s reset.',
    sourceLabel: 'Real Madrid – activation, rondos and possession',
    sourceUrl: 'https://www.realmadrid.com/en-US/news/football/first-team/trainings/el-real-madrid-se-esta-entrenando-08-05-2026',
    sourceType: 'Real Madrid',
  },
  short_passing: {
    label: 'Short passing',
    organisation: 'Passing circuit with changing angles, third-player support and one/two-touch variants.',
    coachingPoints: ['Pass weight', 'Support angle immediately after pass', 'Receive on back foot', 'Play at match tempo'],
    progression: 'Pattern → opposed rondo → overload possession → game with reward for third-player combinations.',
    individualUse: 'Two-wall/partner passing with one-touch and two-touch alternating patterns.',
    teamUse: '4v2 / 5v3 rondos and directional possession with target players.',
    workRest: '3–5 x 2–3 min, 60–90 s rest depending on intensity.',
    sourceLabel: 'Real Madrid – rondos and possession',
    sourceUrl: 'https://www.realmadrid.com/en-US/news/football/first-team/trainings/el-equipo-se-esta-entrenando-22-05-2026',
    sourceType: 'Real Madrid',
  },
  long_passing: {
    label: 'Long passing',
    organisation: 'Progress distance gradually; receive, set and strike diagonal/vertical targets under time pressure.',
    coachingPoints: ['Stable plant foot', 'Strike through appropriate ball line', 'Head up before contact', 'Match the pass to teammate movement'],
    progression: 'Static target → moving target → switch of play → opposed transition game.',
    individualUse: '8–12 quality passes each side to moving target zones.',
    teamUse: '8v8 possession with wide target zones and reward for successful switches.',
    workRest: 'Low repetition/high quality: 4–6 reps each side, 60–90 s between clusters.',
    sourceLabel: 'FIFA Training Centre – Set-piece session passing circuit',
    sourceUrl: 'https://www.fifatrainingcentre.com/en/practice/elite-sessions/youth-national-teams/france-u17-men/wednesday-session-set-pieces.php',
    sourceType: 'FIFA',
  },
  weak_foot: {
    label: 'Weak foot',
    organisation: 'Force the non-dominant foot for receive-pass-finish actions without excessive fatigue.',
    coachingPoints: ['Clean first contact', 'Do not rush progression', 'Use the same technical cues as dominant side', 'Measure success, not volume only'],
    progression: 'Unopposed → moving target → passive pressure → small game with weak-foot scoring bonus.',
    individualUse: 'Short technical micro-dose before team training: 5–10 min.',
    teamUse: 'Small-sided game with bonus points for weak-foot assist/finish.',
    workRest: 'Frequent micro-doses; avoid fatigue-driven poor technique.',
    sourceLabel: 'FIFA Training Centre – Technical Activation',
    sourceUrl: 'https://www.fifatrainingcentre.com/en/practice/technical-activation/technical-activation-part-1--introduction.php',
    sourceType: 'FIFA',
  },
  finishing: {
    label: 'Finishing',
    organisation: 'Finish from realistic arrival angles: cutback, through ball, cross, second ball and transition.',
    coachingPoints: ['Scan goalkeeper', 'Prepare body before final touch', 'Choose power vs placement', 'Rebound reaction'],
    progression: 'Unopposed finishing → passive recovering defender → live 2v1/3v2 → small-sided game.',
    individualUse: '3–4 finishing locations, 4–6 high-quality repetitions per location.',
    teamUse: 'Possession into small goals followed by attack to full goal; transition quickly after regain.',
    workRest: 'Long enough recovery to preserve speed/power; avoid endless low-quality shooting.',
    sourceLabel: 'Real Madrid – possession with finishing + reduced games',
    sourceUrl: 'https://www.realmadrid.com/en-US/news/football/first-team/trainings/el-equipo-se-esta-entrenando-17-07-2026',
    sourceType: 'Real Madrid',
  },
  scanning: {
    label: 'Scanning / awareness',
    organisation: 'Create information before reception using shoulder checks, colour/number cues and pressure arriving from blind side.',
    coachingPoints: ['Scan before and during ball travel', 'Check both shoulders when central', 'Use information to decide first touch', 'Re-scan after pass'],
    progression: 'Cue task → rondo → positional game → game with hidden/late pressure.',
    individualUse: 'Server gives visual cue behind player before the ball arrives.',
    teamUse: 'Positional game with scoring condition for line-breaking receive after visible scan.',
    workRest: 'Cognitive quality: 45–90 s blocks with short reset.',
    sourceLabel: 'FIFA Training Centre – Technical Activation',
    sourceUrl: 'https://www.fifatrainingcentre.com/en/practice/technical-activation/technical-activation-part-1--introduction.php',
    sourceType: 'FIFA',
  },
  decision_making: {
    label: 'Decision making',
    organisation: 'Use variable-number overloads and transition cues so the correct solution changes repeatedly.',
    coachingPoints: ['Recognise numerical superiority', 'Exploit free player', 'Know when to secure possession', 'React instantly to transition'],
    progression: '2v1 → 3v2 → transition game → conditioned 6v6/8v8.',
    individualUse: 'Video/question review + live 1v1/2v1 constraints.',
    teamUse: 'Small-sided games with frequent transition and changing scoring targets.',
    workRest: '3–5 x 2–4 min depending on pitch/player numbers.',
    sourceLabel: 'Real Madrid – reduced-space games and tactical work',
    sourceUrl: 'https://www.realmadrid.com/en-US/news/football/first-team/trainings/el-real-madrid-se-esta-entrenando-08-05-2026',
    sourceType: 'Real Madrid',
  },
  pressing_timing: {
    label: 'Pressing timing',
    organisation: 'Train trigger recognition: backward pass, poor touch, receiver facing own goal, isolated wide player.',
    coachingPoints: ['First presser curves run', 'Second player removes next option', 'Team squeezes behind pressure', 'Regain then attack quickly'],
    progression: '2v2 press cue → 4v4+targets → 6v6 transition → full team phase.',
    individualUse: 'Position-specific trigger recognition and curved approach runs.',
    teamUse: 'Conditioned game rewarding regain within 5–8 seconds after loss.',
    workRest: 'Short intense bouts, e.g. 4–6 x 90–150 s with 60–90 s recovery.',
    sourceLabel: 'Real Madrid – pressing after losing possession',
    sourceUrl: 'https://www.realmadrid.com/en-US/news/football/first-team/trainings/el-equipo-se-esta-entrenando-04-05-2026',
    sourceType: 'Real Madrid',
  },
  defensive_positioning: {
    label: 'Defensive positioning',
    organisation: 'Train body angle, cover distance, compactness and protection of central space in unit and phase play.',
    coachingPoints: ['Protect goal-side/central space first', 'Cover first defender', 'Move as ball moves', 'Communicate line height'],
    progression: '1v1 channel → 2v2/3v3 unit → back-four vs attack → 8v8/11v11 phase.',
    individualUse: 'Defender works against repeated attacker movements with coach freeze/restart feedback.',
    teamUse: 'Phase-of-play exercise with ball moved across zones and scoring for compact regains.',
    workRest: 'Tactical blocks 3–6 min; stop/restart for coaching where necessary.',
    sourceLabel: 'FIFA Training Centre – Position-specific training methodology',
    sourceUrl: 'https://www.fifatrainingcentre.com/en/practice/elite-sessions/youth-national-teams/u15-boys-tournament/matchday-1-mexico-u15-preparing-to-play-spain-in-a-4-4-2-diamond-shape.php',
    sourceType: 'FIFA',
  },
  acceleration: {
    label: 'Acceleration',
    organisation: 'Short maximal accelerations from football-specific start positions with full recovery.',
    coachingPoints: ['Project forward', 'Powerful first steps', 'Full intent', 'Stop if velocity clearly drops'],
    progression: '5–10 m starts → reactive starts → ball chase → opposed race.',
    individualUse: '4–8 x 5–15 m, full recovery between reps.',
    teamUse: 'Reactive acceleration integrated before/within transition game.',
    workRest: 'High quality: ~1:8 to 1:15 work:rest depending on distance.',
    sourceLabel: 'Real Madrid – reaction speed work',
    sourceUrl: 'https://www.realmadrid.com/en-US/news/football/first-team/trainings/el-equipo-se-esta-entrenando-22-05-2026',
    sourceType: 'Real Madrid',
  },
  repeated_sprint: {
    label: 'Repeated-sprint capacity',
    organisation: 'Repeated high-speed bouts with directional football actions; keep a clear distinction between conditioning and pure speed.',
    coachingPoints: ['Quality first rep', 'Efficient deceleration', 'Maintain technical action under fatigue', 'Monitor drop-off'],
    progression: 'Linear repeats → COD repeats → ball-based transition repeats.',
    individualUse: 'Use only when training age/readiness supports high-intensity loading.',
    teamUse: 'Small-sided transition bouts or structured repeated sprint clusters.',
    workRest: 'Dose to player level; speed-quality work needs longer rest than conditioning work.',
    sourceLabel: 'Real Madrid – physical circuits, running and pressing',
    sourceUrl: 'https://www.realmadrid.com/en-US/news/football/first-team/trainings/el-real-madrid-se-esta-entrenando-16-07-2026',
    sourceType: 'Real Madrid',
  },
  concentration: {
    label: 'Concentration',
    organisation: 'Use task constraints that change after coach cue; players must retain tactical role while processing new information.',
    coachingPoints: ['Reset after every action', 'Communicate early', 'React to cue not habit', 'Maintain detail late in block'],
    progression: 'Simple cue → dual cue → transition game → game with changing objectives.',
    individualUse: 'Short perception/reaction tasks attached to technical work.',
    teamUse: 'Conditioned game where target/goals/pressing rule changes on audible cue.',
    workRest: 'Short blocks to maintain cognitive quality; 60–120 s depending on task.',
    sourceLabel: 'Real Madrid – reaction speed + tactical/possession work',
    sourceUrl: 'https://www.realmadrid.com/en-US/news/football/first-team/trainings/el-equipo-se-esta-entrenando-13-05-2026',
    sourceType: 'Real Madrid',
  },
};

export const TEAM_TOPICS: TeamTrainingTopic[] = [
  {
    id: 'attacking_corners',
    label: 'Attacking corners',
    domain: 'set_piece',
    objective: 'Develop repeatable delivery zones, blocks/decoys, second-ball structure and immediate counterpress.',
    sessionStructure: ['Set-piece taker technical warm-up', 'Walk-through positions and timing', 'Live attack v defence repetitions', 'Second-ball and transition finish'],
    coachingPoints: ['Same starting picture, multiple outcomes', 'Synchronise runs with delivery', 'Protect edge of box', 'Define counterpress roles'],
    sourceLabel: 'FIFA – France U17 set-piece session',
    sourceUrl: 'https://www.fifatrainingcentre.com/en/practice/elite-sessions/youth-national-teams/france-u17-men/wednesday-session-set-pieces.php',
  },
  {
    id: 'defending_corners',
    label: 'Defending corners',
    domain: 'set_piece',
    objective: 'Build a clear zonal / player-marking / hybrid structure with ownership of first contact and counter outlet.',
    sessionStructure: ['Walk-through structure', 'Unopposed delivery recognition', 'Live corner repetitions', 'Clearance + counter transition'],
    coachingPoints: ['Protect primary danger zones', 'Goalkeeper communication', 'Attack first contact', 'Assign outlet and second-ball players'],
    sourceLabel: 'FIFA – Club World Cup 2025 defending corner strategies',
    sourceUrl: 'https://www.fifatrainingcentre.com/en/game/tournaments/fcwc/2025/team-analyses/set-plays-defending-corner-strategies.php',
  },
  {
    id: 'attacking_free_kicks',
    label: 'Attacking free-kick variants',
    domain: 'set_piece',
    objective: 'Create a small menu of direct, wide and indirect free-kick solutions for different distances.',
    sessionStructure: ['Taker activation', 'Direct strike technique', 'Wide delivery patterns', 'Live wall/defence repetitions'],
    coachingPoints: ['Power, disguise or precision by distance', 'Clear trigger/call', 'Attack blind side', 'Plan second phase'],
    sourceLabel: 'FIFA – Direct free kicks, short range',
    sourceUrl: 'https://www.fifatrainingcentre.com/en/game/game-analysis/set-plays/set-play-routines/direct-free-kicks-fine-margins-at-short-distance.php',
  },
  {
    id: 'defending_free_kicks',
    label: 'Defending free kicks',
    domain: 'set_piece',
    objective: 'Coordinate wall, line, goalkeeper space and runners against direct and wide free kicks.',
    sessionStructure: ['Positioning walk-through', 'Wide free-kick line work', 'Direct free-kick wall/keeper work', 'Live mixed restarts'],
    coachingPoints: ['Line starts together', 'Protect goalkeeper movement', 'Track blockers and blind-side runners', 'React to second ball'],
    sourceLabel: 'FIFA – Mexico U15 set-piece preparation',
    sourceUrl: 'https://www.fifatrainingcentre.com/en/practice/elite-sessions/youth-national-teams/u15-boys-tournament/matchday-1-mexico-u15-preparing-to-play-spain-in-a-4-4-2-diamond-shape.php',
  },
  {
    id: 'defensive_block',
    label: 'Defensive block / compactness',
    domain: 'tactical',
    objective: 'Improve horizontal/vertical compactness, cover, line height and protection of central progression.',
    sessionStructure: ['Unit movement without opposition', '4v4/6v6 directional game', 'Back line + midfield phase', 'Full team game'],
    coachingPoints: ['Move as the ball moves', 'Protect centre first', 'Distances between units', 'Recognise press vs drop moment'],
    sourceLabel: 'FIFA – Position-specific / game-phase methodology',
    sourceUrl: 'https://www.fifatrainingcentre.com/en/practice/elite-sessions/youth-national-teams/u15-boys-tournament/matchday-1-mexico-u15-preparing-to-play-spain-in-a-4-4-2-diamond-shape.php',
  },
  {
    id: 'pressing',
    label: 'Team pressing & counterpress',
    domain: 'tactical',
    objective: 'Synchronise pressure, cover and squeeze on clear triggers and immediately after possession loss.',
    sessionStructure: ['Trigger walk-through', '4v4+targets', '6v6 transition game', 'Phase/game application'],
    coachingPoints: ['Curved first press', 'Lock next pass', 'Back line squeezes', 'Regain then attack'],
    sourceLabel: 'Real Madrid – pressing after losing possession',
    sourceUrl: 'https://www.realmadrid.com/en-US/news/football/first-team/trainings/el-equipo-se-esta-entrenando-04-05-2026',
  },
  {
    id: 'build_up',
    label: 'Build-up against pressure',
    domain: 'tactical',
    objective: 'Create repeatable structures to attract pressure, find the free player and progress through/around the press.',
    sessionStructure: ['Position-specific passing activation', 'Overload build-up', '7v6/8v7 phase', 'Full game from goalkeeper restarts'],
    coachingPoints: ['Create width/depth early', 'Scan before receive', 'Third-player solution', 'Know when to play through vs around'],
    sourceLabel: 'Real Madrid – ball circulation and pressing',
    sourceUrl: 'https://www.realmadrid.com/en-US/news/football/first-team/trainings/el-equipo-se-esta-entrenando-17-07-2026',
  },
  {
    id: 'transition_defence',
    label: 'Defensive transition',
    domain: 'tactical',
    objective: 'Improve reaction after loss: immediate pressure when possible, otherwise recover compactly and protect the centre.',
    sessionStructure: ['2v2/3v3 transition', '4v4+neutral transition', '6v6 directional game', 'Full game with transition scoring rule'],
    coachingPoints: ['First 2–3 seconds decisive', 'Nearest player pressures', 'Others protect inside', 'Sprint recovery if press is broken'],
    sourceLabel: 'Real Madrid – pressing/ball recovery and reduced games',
    sourceUrl: 'https://www.realmadrid.com/en-US/news/football/first-team/trainings/el-real-madrid-se-esta-entrenando-16-07-2026',
  },
];

export function trainingForWeakness(weakness: string) {
  return PLAYER_TRAINING[weakness] || PLAYER_TRAINING.decision_making;
}
